import React, { Component } from 'react'

import { DateUtils } from 'react-day-picker'
import DayPickerInput from 'react-day-picker/DayPickerInput'
import { withTranslation } from 'react-i18next';
import styled from 'styled-components'
import 'moment/locale/es'
import { v4 as uuid } from 'uuid'
// eslint-disable-next-line import/order
import moment from 'moment'
// eslint-disable-next-line import/order
import { formatDate, parseDate } from 'react-day-picker/moment'

import { ErrorContainer } from 'components/common'
import DatePickerNavbar from 'components/datepicker/date-picker-navbar'
import { InputSearchDate } from 'components/input'
import Message from 'components/message'
import arialabels from 'constants/arialabels'
import { getDayJsObject } from 'helpers/dates'

import {
  DatePickerContainer,
  DatePickerFromContainer,
  DatePickerToContainer,
  DatePickerTitle,
  InputContainers
} from '../styled'

const StyledDatePickerInput = styled(DayPickerInput)`
  .DayPickerInput {
    width: ${({ theme }) =>
      theme?.root?.style.getPropertyValue('--show-arsa-keys-is-activated') === 'true'
        ? '340px'
        : '100%'};
  }
`

class DateRangePicker extends Component {
  constructor(props) {
    super(props)

    this.state = {
      from: this.props.value ? this.props.value.from : undefined,
      to: this.props.value ? this.props.value.to : undefined,
      enteredTo: this.props.value ? this.props.value.to : undefined
    }

    this.to = React.createRef()
    this.from = React.createRef()
  }

  componentDidMount() {
    this.fromId = uuid()
    this.toId = uuid()
    this.from.current
      .getInput()
      .input.addEventListener('keydown', props => this.handleTabPress(props, this.from.current))
    this.to.current
      .getInput()
      .input.addEventListener('keydown', props => this.handleTabPress(props, this.to.current))
    document.addEventListener('mousedown', this.handleBlur)
    document.addEventListener('focusin', this.handleBlur)
  }

  /**
   * This method will add a specific class to the first and the last day of each month.
   */
  componentDidUpdate(prevProps) {
    document.querySelectorAll('.DayPicker-Month').forEach(month => {
      const days = [...month.querySelectorAll('.DayPicker-Day:not(.DayPicker-Day--outside)')]
      const first = days.shift()
      const last = days.pop()

      first.classList.add('DayPicker-Day--first')
      last.classList.add('DayPicker-Day--last')
    })

    if (prevProps.value !== this.props.value) {
      this.setState({
        from: this.props.value ? this.props.value.from : undefined,
        to: this.props.value ? this.props.value.to : undefined,
        enteredTo: this.props.value ? this.props.value.to : undefined
      })
    }
  }

  /**
   * Focus to `to` field. A timeout is required here because the overlays already set timeouts to
   * work well with input fields.
   */
  focusTo = () => {
    this.timeout = setTimeout(() => this.to.current.getInput().focus(), 0)
  }

  blurTo = () => {
    this.to.current.getInput().blur()
  }

  componentWillUnmount() {
    clearTimeout(this.timeout)
    this.from.current
      .getInput()
      .input.removeEventListener('keydown', props => this.handleTabPress(props, this.from.current))
    this.to.current
      .getInput()
      .input.removeEventListener('keydown', props => this.handleTabPress(props, this.to.current))
    document.removeEventListener('mousedown', this.handleBlur)
    document.removeEventListener('focusin', this.handleBlur)
  }

  getCustomOverlay = (id, { classNames, children, ...props }) => {
    // eslint-disable-next-line
    const { selectedDay, tabIndex, ...otherProps } = props

    return (
      <div className={classNames.overlayWrapper} id={id} tabIndex={-1} {...otherProps}>
        <div className={classNames.overlay}>{children}</div>
      </div>
    )
  }

  captionElement = ({ date }) => {
    const { i18n: { language }} = this.props
    const monthName = date.toLocaleDateString(language, { month: 'long' }).toUpperCase()
    const year = date.toLocaleDateString(language, { year: 'numeric' }).toUpperCase()

    return (
      <div className="DayPicker-Caption" role="heading" aria-level="1">
        <div>{`${monthName} ${year}`}</div>
      </div>
    )
  }

  showFromMonth = () => {
    const { from, to } = this.state

    if (from && moment(to).diff(moment(from), 'months') < 2) {
      this.to.current.getDayPicker().showMonth(from)
    }

    this.props.onChange({ from, to })
  }

  isSelectingFirstDay(from, to, day) {
    const isBeforeFirstDay = from && DateUtils.isDayBefore(day, from)
    const isRangeSelected = from && to

    return !from || isBeforeFirstDay || isRangeSelected
  }

  handleDayMouseEnter = day => {
    const { from, to } = this.state

    if (!this.isSelectingFirstDay(from, to, day)) {
      this.setState({ enteredTo: day })
    }
  }

  handleDayPickerHide = () => {
    if (!this.state.to) {
      this.setState({ enteredTo: null })
    }
  }

  handleTabPress = (event, ref) => {
    if (event.key == 'Tab' && !event.shiftKey) {
      event.stopImmediatePropagation()
      ref.getDayPicker().focus()
    }
  }

  handleBlur = event => {
    if (
      !this.from.current.getInput()?.input.contains(event.target) &&
      !this.from.current.getDayPicker()?.dayPicker.contains(event.target)
    ) {
      this.from.current.hideDayPicker()
    }

    if (
      !this.to.current.getInput()?.input.contains(event.target) &&
      !this.to.current.getDayPicker()?.dayPicker.contains(event.target)
    ) {
      this.to.current.hideDayPicker()
    }
  }

  onFromChangeCallback() {
    const { from, to } = this.state

    if (from && to) {
      this.props.onChange({ from, to })
    }
  }

  handleFromChange = from => {
    this.setState({ from }, () => this.onFromChangeCallback())

    if (this.state.to && this.state.to < from) {
      this.setState({ to: from }, () => this.onFromChangeCallback())
    }
  }

  handleToChange = to => this.setState({ to, enteredTo: to }, this.showFromMonth)

  handleResetClick = () => this.setState({ from: undefined, to: undefined, enteredTo: undefined })

  isDisabledForRange = day => {
    const cond1 = day < this.state.from
    return cond1 || this.props.disabledDays(day)
  }

  getToFieldDisabledDays() {
    if (this.props.disabledDays) {
      const isFunc = typeof this.props.disabledDays === 'function'

      if (isFunc) {
        return this.isDisabledForRange
      } else {
        const res = {
          ...this.props.disabledDays,
          before: this.state.from || this.props.disabledDays.before
        }

        return res
      }
    }

    return {
      before: this.state.from || getDayJsObject().toDate()
    }
  }

  getSelectedDaysTo = () => [this.state.from, { from: this.state.from, to: this.state.enteredTo }]

  getSelectedDaysFrom = () => (this.state.to ? this.getSelectedDaysTo() : [this.state.from])

  render() {
    const { from, to, enteredTo } = this.state
    const modifiers = { start: from, end: enteredTo }
    const inputProps = {
      flatStyle: this.props.flatStyle,
      tabIndex: this.props.disableIndex ? -1 : 0
    }

    return (
      <DatePickerContainer>
        <InputContainers>
          <DatePickerFromContainer width={this.props.width}>
            <DatePickerTitle
              semiBold={this.props.semiBold}
              forId={arialabels.DATE_RANGE_PICKER_FROM}
            >
              {this.props.fromLabel}
            </DatePickerTitle>
            <StyledDatePickerInput
              onDayPickerHide={this.handleDayPickerHide}
              component={InputSearchDate}
              inputProps={{
                ...inputProps,
                name: 'from-date',
                dateError: this.props.fromError,
                id: arialabels.DATE_RANGE_PICKER_FROM,
                'aria-controls': this.fromId
              }}
              overlayComponent={props => this.getCustomOverlay(this.fromId, props)}
              ref={this.from}
              keepFocus={false}
              value={from}
              placeholder={this.props.placeholder}
              format={this.props.format}
              formatDate={formatDate}
              parseDate={parseDate}
              onDayChange={this.handleFromChange}
              dayPickerProps={{
                locale: this.props.locale,
                selectedDays: this.getSelectedDaysFrom(),
                modifiers,
                numberOfMonths: 2,
                ...this.props.dayPickerProps,
                onDayClick: () => this.focusTo(),
                onDayMouseEnter: this.handleDayMouseEnter,
                captionElement: this.captionElement,
                disabledDays: this.props.disabledDays,

                navbarElement: props => (
                  <DatePickerNavbar
                    {...props}
                    fromMonth={this.props.fromMonth}
                    toMonth={this.props.toMonth}
                  />
                ),

                fromMonth: this.props.fromMonth,
                initialMonth: this.props.initialMonth,
                toMonth: this.props.toMonth
              }}
            />
          </DatePickerFromContainer>
          <DatePickerToContainer width={this.props.width}>
            <DatePickerTitle semiBold={this.props.semiBold} forId={arialabels.DATE_RANGE_PICKER_TO}>
              {this.props.toLabel}
            </DatePickerTitle>
            <StyledDatePickerInput
              onDayPickerHide={this.handleDayPickerHide}
              component={InputSearchDate}
              inputProps={{
                ...inputProps,
                name: 'to-date',
                dateError: this.props.toError,
                id: arialabels.DATE_RANGE_PICKER_TO,
                'aria-controls': this.toId
              }}
              overlayComponent={props => this.getCustomOverlay(this.toId, props)}
              ref={this.to}
              keepFocus={false}
              value={to}
              placeholder={this.props.placeholder}
              format={this.props.format}
              formatDate={formatDate}
              parseDate={parseDate}
              onDayChange={this.handleToChange}
              dayPickerProps={{
                locale: this.props.locale,
                selectedDays: this.getSelectedDaysTo(),
                modifiers,
                month: from,
                fromMonth: from || this.props.fromMonth,
                initialMonth: from || this.props.initialMonth,
                toMonth: this.props.toMonth,
                numberOfMonths: 2,
                ...this.props.dayPickerProps,
                onDayMouseEnter: this.handleDayMouseEnter,
                captionElement: this.captionElement,
                navbarElement: props => (
                  <DatePickerNavbar
                    {...props}
                    fromMonth={this.props.fromMonth}
                    toMonth={this.props.toMonth}
                  />
                ),
                disabledDays: this.getToFieldDisabledDays(),
                onBlur: () => this.blurTo(),
                onDayClick: () => this.blurTo()
              }}
            />
          </DatePickerToContainer>
        </InputContainers>
        {this.props.fromError && (
          <ErrorContainer>
            <Message type="formInputError">{this.props.fromError}</Message>
          </ErrorContainer>
        )}
        {this.props.toError && !this.props.fromError && (
          <ErrorContainer>
            <Message type="formInputError">{this.props.toError}</Message>
          </ErrorContainer>
        )}
      </DatePickerContainer>
    )
  }
}

export default withTranslation()(DateRangePicker)
